import { useEffect, useRef, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { atomFamily, useRecoilState } from 'recoil'
import debounce from 'lodash/debounce'

import { regexEmail, storeGet } from 'mailup-react-helpers'
import { STORE_KEY_LOGIN_TYPE } from 'mailup-react-helpers'

import {
  toastShow,
  TOAST_VARIANT_SUCCESS,
  TOAST_VARIANT_ERROR,
  TRUSTED_EMAIL_INPUT_VARIANT_NEUTRAL,
  TRUSTED_EMAIL_INPUT_VARIANT_INVALID,
  TRUSTED_EMAIL_INPUT_VARIANT_SUCCESS,
  MESSAGE_BOX_VARIANT_DANGER,
  MESSAGE_BOX_VARIANT_SUCCESS
} from 'mailup-web-components'

import {
  beSendRegisterSender,
  TRUSTED_SENDER_STATUS_DELIVER_ERROR,
  TRUSTED_SENDER_STATUS_DELIVER_ISP
} from 'Api/beSendGoApi'

import {
  beAccountSettingsGetUserSetting,
  beAccountSettingsUpsertUserSettings
} from 'Api/beAccountSettingsApi'
import { LOGIN_TYPE_WEBLOGIN } from 'Constants/'

import { logAppError } from 'Helpers/logger'

import fetchStatus from './fetchStatus'
import statusPolling from './statusPolling'

import mapStatusToVariant from './mapStatusToVariant'

export const trustedEmailSenderState = atomFamily({
  key: 'trustedEmailSenderState',
  default: {
    value: '',
    isError: false,
    isDirty: false,
    invalidRequired: false,
    invalidFormat: false,
    variant: TRUSTED_EMAIL_INPUT_VARIANT_NEUTRAL,
    status: undefined,
    currentTimer: undefined,
    messageBoxOpen: false,
    messageBoxType: TOAST_VARIANT_SUCCESS
  }
})

export async function registerSender(email) {
  const result = await beSendRegisterSender(email)

  return result
}

export async function fetchHideDelivStatus(email) {
  const result = await beAccountSettingsGetUserSetting(
    0,
    `HideDeliverabilityStatus-${email}`
  )

  return result ? result.DataValue === true : false
}

export async function setHideDelivStatus(email) {
  const loginType = storeGet(STORE_KEY_LOGIN_TYPE)
  if (loginType === LOGIN_TYPE_WEBLOGIN) {
    await beAccountSettingsUpsertUserSettings(
      0,
      `HideDeliverabilityStatus-${email}`,
      true
    )
  }
}

const useTrustedEmailSender = (
  itemKey,
  initialValue = '',
  required = false,
  showMessageBox = undefined
) => {
  const intl = useIntl()
  const timer = useRef(null)

  const [
    { value, variant, invalidRequired, invalidFormat, status, currentTimer },
    setState
  ] = useRecoilState(trustedEmailSenderState(itemKey))

  const handleChange = ({ target: { value } }) => {
    innerHandleChange(value)
  }

  const updateStatus = async _email => {
    const [statusResult, hideDelivStatus] = await Promise.all([
      fetchStatus(_email),
      fetchHideDelivStatus(_email)
    ])

    setState(prev => ({
      ...prev,
      variant: mapStatusToVariant(
        statusResult.Status,
        hideDelivStatus,
        statusResult.ApplyOverrideDomain
      ),
      status: statusResult.Status
    }))
  }

  const debouncedUpdateStatus = useMemo(() => debounce(updateStatus, 250), [])

  const innerHandleChange = async email => {
    const validRequired = required ? !!email : true
    const validEmail = email && email.match(regexEmail)
    try {
      if (validEmail) {
        setState(prev => ({
          ...prev,
          value: email,
          invalidRequired: false,
          invalidFormat: false
        }))

        debouncedUpdateStatus(email)
      } else {
        setState(prev => ({
          ...prev,
          value: email,
          invalidRequired: !validRequired,
          invalidFormat: !validEmail,
          variant:
            validRequired && validEmail
              ? TRUSTED_EMAIL_INPUT_VARIANT_NEUTRAL
              : TRUSTED_EMAIL_INPUT_VARIANT_INVALID
        }))
      }
    } catch (error) {
      console.error('### error', error)

      clearTimeout(timer.current)

      logAppError({
        appError: error,
        Component: 'useTrustedEmailSender',
        Method: 'innerHandleChange'
      })

      setState(prev => ({
        ...prev,
        isError: true,
        value: email
      }))
    }
  }

  const handleDismissWarningClick = async () => {
    try {
      await setHideDelivStatus(value)
    } catch (error) {
      console.error('### error', error)
      logAppError({
        appError: error,
        Component: 'useTrustedEmailSender',
        Method: 'handleDismissWarningClick'
      })
    }
    setState(prev => ({
      ...prev,
      variant: TRUSTED_EMAIL_INPUT_VARIANT_SUCCESS
    }))
  }

  const handleVerifyClick = async () => {
    try {
      await registerSender(value)

      if (showMessageBox && typeof showMessageBox === 'function') {
        showMessageBox(MESSAGE_BOX_VARIANT_SUCCESS)
      } else {
        toastShow(
          intl.formatMessage({
            id: 'Load.Common.EmailSendMessage'
          }),
          TOAST_VARIANT_SUCCESS
        )
      }

      setState(prev => ({
        ...prev,
        currentTimer: undefined
      }))
      timer.current = await statusPolling(value, setState)
    } catch (error) {
      console.error('### error', error)

      clearTimeout(timer.current)

      if (typeof showMessageBox === 'function') {
        showMessageBox(MESSAGE_BOX_VARIANT_DANGER)
      } else {
        toastShow(
          intl.formatMessage({
            id: 'Load.Common.LoadingDataError'
          }),
          TOAST_VARIANT_ERROR
        )
      }

      logAppError({
        appError: error,
        Component: 'useTrustedEmailSender',
        Method: 'handleVerifyClick'
      })
    }
  }

  useEffect(() => {
    return () => {
      clearTimeout(timer.current)
      debouncedUpdateStatus.cancel()
    }
  }, [])

  // useEffect(() => {
  //   currentTimer && clearTimeout(currentTimer)
  // }, [currentTimer])

  useEffect(() => {
    initialValue && innerHandleChange(initialValue)
  }, [initialValue])

  const i18n = {
    invalid: invalidRequired
      ? intl.formatMessage({ id: 'Load.TrustedEmailInput.EmptyMessage' })
      : invalidFormat
      ? intl.formatMessage({
          id: 'Load.TrustedEmailInput.WrongFormatMessage'
        })
      : undefined,
    unverified: intl.formatMessage({
      id: 'Load.TrustedEmailInput.NotConfirmed'
    }),
    verifyLink: intl.formatMessage({
      id: 'Load.TrustedEmailInput.RequestConfirmation'
    }),
    error: intl.formatMessage({
      id: 'Load.TrustedEmailInput.DeliverabilityCheckError'
    }),
    errorTooltip:
      status === TRUSTED_SENDER_STATUS_DELIVER_ERROR
        ? intl.formatMessage({ id: 'Load.Deliverability.Error' })
        : status === TRUSTED_SENDER_STATUS_DELIVER_ISP
        ? intl.formatMessage({
            id: 'Load.Deliverability.Isp'
          })
        : undefined,
    warning: intl.formatMessage({
      id: 'Load.TrustedEmailInput.DeliverabilityCheckWarning'
    }),
    warningTooltip: intl.formatMessage({ id: 'Load.Deliverability.Warning' }),
    blocked: intl.formatMessage({ id: 'Load.TrustedEmailInput.Blocked' }),
    info: intl.formatMessage({
      id: 'Load.TrustedEmailInput.DeliverabilityCheckMuShield'
    }),
    infoTooltip: intl.formatMessage({
      id: 'Load.TrustedEmailInput.DeliverabilityCheckMuShieldTooltip'
    })
  }

  return {
    value,
    onChange: handleChange,
    onDismissWarningClick: handleDismissWarningClick,
    onVerifyClick: handleVerifyClick,
    variant,
    i18n
  }
}

export default useTrustedEmailSender
